import pluralize from "pluralize";
import {useMemo} from "react";
import {FiCheck} from "react-icons/fi";
import {Link} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {AnalyzerPhase, AnalyzerUsageChipState} from "@/pages/analyzer/Analyzer.types.ts";
import {
  checksUrlForTags,
  getUsageChipState,
  getUsageChips,
  hasCheckSelectionEvent,
  isProposingChecks,
} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerCreateConfirmDialog} from "@/pages/analyzer/components/AnalyzerCreateConfirmDialog.tsx";
import {AnalyzerUpgradeModal} from "@/pages/analyzer/components/AnalyzerUpgradeModal.tsx";
import {AnalyzerUsageChip} from "@/pages/analyzer/components/AnalyzerUsageChip.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";
import {useBulkCreateChecks} from "@/pages/analyzer/hooks/useBulkCreateChecks.ts";
import {LimitCheckServiceLimit} from "@/pages/billing/v2/InstantCheckout/LimitCheckServiceLimit.tsx";

import styles from "./AnalyzerActionBar.module.scss";

export const AnalyzerActionBar = () => {
  const {user} = useCurrentUserContext();
  const {phase, resultsEvents, proposedChecks, getTier, creationOutcome} = useAnalyzerContext();
  const {
    methods,
    isCreating,
    canCreate,
    canPurchase,
    createLabel,
    isGrowOnly,
    newSelection,
    selectedRumCount,
    createdRumCount,
    snippets,
    handleCreate,
    openUpgradeModal,
    showConfirm,
    confirmCreate,
    cancelConfirm,
    goToChecks,
    limitCheckRef,
    overLimitStatuses,
    dismissOverLimit,
  } = useBulkCreateChecks();
  const hasWriteAccess = !!user?.has_write_access;

  const chips = useMemo(
    () => getUsageChips(proposedChecks, newSelection, getTier),
    [proposedChecks, newSelection, getTier]
  );
  const isOverLimit = chips.some((chip) => getUsageChipState(chip) === AnalyzerUsageChipState.OVER);
  const isGenerating = phase === AnalyzerPhase.STREAMING && !hasCheckSelectionEvent(resultsEvents);
  const createdCount = Object.keys(creationOutcome?.createdByProposalId ?? {}).length;
  const failedCount = Object.keys(creationOutcome?.errorsByProposalId ?? {}).length;
  const tags = methods.watch("tags");
  const checksUrl = checksUrlForTags(creationOutcome?.tagIds ?? []);
  const goToChecksLink = (
    <Button as={Link} to={checksUrl} variant="linkInherit" size="fitContent" typography="linkBold">
      Go to your Checks
    </Button>
  );

  return (
    <div>
      {isOverLimit && hasWriteAccess && (
        <Alert
          variant="error"
          size="banner"
          typography="bodySmall"
          callout="Your selected Checks exceed your plan limits"
          action={
            <Button variant="linkInherit" size="fitContent" typography="linkBold" onClick={openUpgradeModal}>
              Upgrade Plan
            </Button>
          }
        >
          Deselect Checks or Upgrade
        </Alert>
      )}
      {failedCount === 0 && createdRumCount > 0 && (
        <Alert
          variant="verification"
          size="banner"
          typography="bodySmall"
          callout={`${createdCount} ${pluralize("Check", createdCount)} created`}
          action={goToChecksLink}
        >
          Install the RUM {pluralize("snippet", createdRumCount)} for {pluralize("Check", createdRumCount, true)} on
          your site to start collecting data — Get snippet in the table shows the code.
        </Alert>
      )}
      {failedCount > 0 && (
        <Alert
          variant="maintenance"
          size="banner"
          typography="bodySmall"
          callout={`${createdCount} ${pluralize("Check", createdCount)} created, ${failedCount} failed`}
          action={goToChecksLink}
        >
          Failed rows show the reason in the Status column. Create only retries the failed ones.
        </Alert>
      )}
      <div className={styles.bar}>
        <div className={styles.chips}>
          {proposedChecks.length > 0 && chips.map((chip) => <AnalyzerUsageChip key={chip.tierKey} chip={chip} />)}
        </div>
        {hasWriteAccess && (
          <Button
            variant="primary"
            size="small"
            disabled={isGenerating || isCreating || !canCreate}
            onClick={handleCreate}
          >
            {isGenerating ? (
              isProposingChecks(resultsEvents) ? (
                "Preparing Checks..."
              ) : (
                "Analyzing..."
              )
            ) : (
              <>
                <Icon icon={FiCheck} />
                {createLabel}
              </>
            )}
          </Button>
        )}
      </div>
      {hasWriteAccess && (
        <>
          <LimitCheckServiceLimit
            ref={limitCheckRef}
            onUpgradeSuccess={handleCreate}
            triggerContext="Assistant Check Creator"
          />
          <AnalyzerUpgradeModal statuses={overLimitStatuses} canPurchase={canPurchase} onClose={dismissOverLimit} />
          <AnalyzerCreateConfirmDialog
            show={showConfirm}
            actionLabel={createLabel}
            isGrowOnly={isGrowOnly}
            rumCount={selectedRumCount}
            tags={tags}
            loading={isCreating}
            snippets={snippets}
            hasFailures={failedCount > 0}
            onConfirm={confirmCreate}
            onCancel={cancelConfirm}
            onGoToChecks={goToChecks}
          />
        </>
      )}
    </div>
  );
};
